import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, MessageCircle, Send, Building2 } from "lucide-react";
import SEO from "@/components/SEO";
import { COMPANY, MARKETPLACES, PREMIUM_MARKETPLACES, QUICK_COMMERCE } from "@/data/content";
import { forwardLeadToWhatsApp } from "@/lib/whatsapp";
import GoogleReviewsCard from "@/components/GoogleReviewsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_MARKETS = [...MARKETPLACES, ...PREMIUM_MARKETPLACES, ...QUICK_COMMERCE].map((m) => m.name);

const TYPE_TITLES = {
  audit: "Free Account Audit",
  start: "Start Selling",
  expert: "Talk to an Expert",
  onboard: "Marketplace Onboarding",
  devproject: "Web / App Project",
};

export default function Contact() {
  const [params] = useSearchParams();
  const type = params.get("type") || "expert";
  const mp = params.get("mp") || "";
  const heading = TYPE_TITLES[type] || "Talk to an Expert";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    marketplace: mp,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source: type });
      toast.success("Thank you! Redirecting you to WhatsApp...");
      forwardLeadToWhatsApp({ ...form, source: type });
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  const waNumber = COMPANY.whatsapp.replace(/\D/g, "");
  const waText = encodeURIComponent(COMPANY.whatsappMessage);

  return (
    <>
      <SEO
        title="Contact — Talk to an E-commerce Expert"
        description="Talk to Orina Enterprise about marketplace onboarding, account management, ads or web/app development. Mumbai office or call us directly."
        path="/contact"
      />

      <section className="border-b-2 border-black bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Contact</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            {heading}.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-black/70 md:text-lg">
            Tell us about your brand, your channels, and your goal. We'll respond within one working day.
          </p>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <GoogleReviewsCard className="mb-10 max-w-3xl" />
        </div>
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-12 md:px-8">
          {/* FORM */}
          <div className="md:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="card-brutal space-y-4 p-6 md:p-10"
            >
              {done ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <div className="display text-3xl font-black md:text-4xl">Thank you 🎉</div>
                  <p className="mt-3 text-base text-black/70">
                    We've opened WhatsApp with your details — drop the message to continue. Our team will also call {COMPANY.phone} or email you at <strong>{COMPANY.email}</strong> within 24 hours.
                  </p>
                  <a
                    href={`https://wa.me/${COMPANY.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-brutal mt-6 inline-flex"
                    style={{ background: "#25D366", color: "#fff", borderColor: "#000" }}
                  >
                    <MessageCircle size={16} /> Open WhatsApp again
                  </a>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="contact-field-name" className="text-xs font-bold uppercase tracking-widest">Full name *</label>
                      <input
                        data-testid="contact-name"
                        required
                        name="name"
                        id="contact-field-name"
                        value={form.name}
                        onChange={onChange}
                        className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-field-email" className="text-xs font-bold uppercase tracking-widest">Email *</label>
                      <input
                        data-testid="contact-email"
                        required
                        type="email"
                        name="email"
                        id="contact-field-email"
                        value={form.email}
                        onChange={onChange}
                        className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="contact-field-phone" className="text-xs font-bold uppercase tracking-widest">Phone *</label>
                      <input
                        data-testid="contact-phone"
                        required
                        name="phone"
                        id="contact-field-phone"
                        placeholder="+91 ..."
                        value={form.phone}
                        onChange={onChange}
                        className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-field-company" className="text-xs font-bold uppercase tracking-widest">Company</label>
                      <input
                        data-testid="contact-company"
                        name="company"
                        id="contact-field-company"
                        value={form.company}
                        onChange={onChange}
                        className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-field-marketplace" className="text-xs font-bold uppercase tracking-widest">Marketplace of interest</label>
                    <select
                      data-testid="contact-marketplace"
                      name="marketplace"
                      id="contact-field-marketplace"
                      value={form.marketplace}
                      onChange={onChange}
                      className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                    >
                      <option value="">Select (optional)</option>
                      {ALL_MARKETS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                      <option value="Multiple">Multiple marketplaces</option>
                      <option value="WebApp">Website / App project</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-field-message" className="text-xs font-bold uppercase tracking-widest">Tell us about your goal *</label>
                    <textarea
                      data-testid="contact-message"
                      required
                      name="message"
                      id="contact-field-message"
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      placeholder="What are you trying to launch, fix or scale?"
                      className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                    />
                  </div>

                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submitting}
                    className="btn-brutal w-full disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Send request"} <Send size={16} />
                  </button>
                  <p className="text-center text-xs text-black/60">
                    By submitting you agree to our Privacy Policy. We'll never spam you.
                  </p>
                </>
              )}
            </form>
          </div>

          {/* SIDE INFO */}
          <aside className="md:col-span-5">
            <div className="card-brutal !bg-black p-8 text-white">
              <div className="display text-xl font-black uppercase tracking-widest text-[#FFD700]">Reach Us</div>
              <ul className="mt-6 space-y-5">
                <li className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[#FFD700] bg-[#FFD700] text-black"><MapPin size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">Office</div>
                    <div className="text-sm text-white/85">{COMPANY.address}</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[#FFD700] bg-[#FFD700] text-black"><Phone size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">Phone (calls)</div>
                    <a href={`tel:${COMPANY.phoneTel}`} className="text-sm text-white/90 hover:text-[#FFD700]">{COMPANY.phone}</a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[#FFD700] bg-[#FFD700] text-black"><MessageCircle size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">WhatsApp</div>
                    <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="text-sm text-white/90 hover:text-[#FFD700]">{COMPANY.whatsappDisplay}</a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[#FFD700] bg-[#FFD700] text-black"><Mail size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="text-sm text-white/90 hover:text-[#FFD700]">{COMPANY.email}</a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[#FFD700] bg-[#FFD700] text-black"><Building2 size={16} /></div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">Hours</div>
                    <div className="text-sm text-white/85">Mon–Sat · 10:00 – 19:00 IST</div>
                  </div>
                </li>
              </ul>

              <a
                data-testid="contact-whatsapp"
                href={`https://wa.me/${waNumber}?text=${waText}`}
                target="_blank"
                rel="noreferrer"
                className="btn-brutal mt-8 inline-flex w-full justify-center"
                style={{ background: "#25D366", color: "#fff", borderColor: "#FFD700", boxShadow: "4px 4px 0 0 #FFD700" }}
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>

            <div className="card-brutal mt-6 overflow-hidden p-0">
              <iframe
                data-testid="contact-map"
                title="Orina Enterprise Office"
                src="https://www.google.com/maps?q=Chincholi+Bunder+Rd,+Malad+West,+Mumbai,+Maharashtra+400064&output=embed"
                width="100%"
                height="320"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
